import React, { useEffect, useMemo, useState } from 'react';
import Typography from '@material-ui/core/Typography';
import TextField from '@material-ui/core/TextField';
import Button from '@material-ui/core/Button';
import Chip from '@material-ui/core/Chip';
import Tooltip from '@material-ui/core/Tooltip';
import Checkbox from '@material-ui/core/Checkbox';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Dialog from '@material-ui/core/Dialog';
import DialogTitle from '@material-ui/core/DialogTitle';
import DialogContent from '@material-ui/core/DialogContent';
import DialogActions from '@material-ui/core/DialogActions';
import { INK } from '../../Utils/styleTokens';
import {
  listCustomDetectors,
  uploadCustomDetector,
  testCustomDetector,
  deleteCustomDetector,
} from '../api';

// Name validation mirrors the discover worker's rule exactly (custom_detectors.detector_name),
// which is the same rule the masking worker uses for custom algorithms.
const NAME_REGEX = /^[A-Za-z][A-Za-z0-9_]{2,63}$/;

const STATUS_STYLES = {
  pending: { background: '#FEF3C7', color: '#B45309' },
  active: { background: '#DCFCE7', color: '#15803D' },
  error: { background: '#FEE2E2', color: '#B91C1C' },
};

// The worker's stackSummary is a list of lines (leading line is "type: message"); the masking
// worker sends a single string. Accept either so the error block renders the same way.
const joinStack = (stackSummary) => {
  if (!stackSummary) return null;
  return Array.isArray(stackSummary) ? stackSummary.join('\n') : String(stackSummary);
};

// Status chip. Unlike custom algorithms there is no "pending test" state — the worker smoke-
// tests at upload, so a listed detector is Active unless it (a) tripped the failure quarantine,
// (b) was deactivated by a failed re-test, or (c) failed to (re)load its class (e.g. after a
// discover-worker restart with a jar that has since gone missing).
const StatusChip = ({ detector }) => {
  if (detector.quarantined) {
    return (
      <Tooltip title={detector.lastTestError || 'Repeated failures during discovery — run a test to restore.'}>
        <Chip size='small' label='Quarantined' style={STATUS_STYLES.error} />
      </Tooltip>
    );
  }
  if (!detector.active) {
    return (
      <Tooltip title={detector.lastTestError || 'Deactivated after a failed test — run a test to restore.'}>
        <Chip size='small' label='Inactive' style={STATUS_STYLES.pending} />
      </Tooltip>
    );
  }
  if (detector.loaded) {
    return <Chip size='small' label='Active' style={STATUS_STYLES.active} />;
  }
  return (
    <Tooltip title={detector.loadError || 'Failed to load'}>
      <Chip size='small' label='Load error' style={STATUS_STYLES.error} />
    </Tooltip>
  );
};

const lastTestSummary = (detector) => {
  if (!detector.lastTestAt) return '—';
  let when = detector.lastTestAt;
  try {
    when = new Date(detector.lastTestAt).toLocaleString();
  } catch {
    // keep raw value
  }
  return detector.lastTestSuccess ? `Passed · ${when}` : `Failed · ${when}`;
};

// Custom Detectors screen (Advanced Config). Lets admins upload a jar implementing the discover
// worker's CustomEntityDetector SPI. The worker smoke-tests the jar at upload; on success the
// detector runs alongside the model in every discovery and its entity types appear in the
// discovery catalog and the Algorithm Mapping tab immediately. Mirrors the Custom Algorithms
// screen minus Edit (the worker has no edit endpoint — replace is a re-upload) and minus the
// force-delete guard (an orphaned entity→algorithm mapping key is harmless).
const CustomDetectorsScreen = ({ canEdit }) => {
  const [detectors, setDetectors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [testTarget, setTestTarget] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const load = async () => {
    try {
      const list = await listCustomDetectors();
      setDetectors(Array.isArray(list) ? list : []);
      setError(null);
    } catch (e) {
      setError(e?.body?.message || e?.message || 'Failed to load custom detectors');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const existingNames = useMemo(
    () => new Set(detectors.map((d) => d.name).filter(Boolean)),
    [detectors],
  );

  return (
    <div>
      <h1 className='adv-config-screen-title'>Custom Detectors</h1>
      <p className='adv-config-screen-desc'>
        Upload a custom entity-detector jar. It runs alongside the built-in model in every
        discovery, and its entity types join the discovery catalog and the Algorithm Mapping tab
        as soon as the upload smoke test passes.
      </p>

      <div className='adv-config-card' style={{ background: '#F6F7F9' }}>
        <Typography variant='body2' style={{ fontWeight: 600, marginBottom: 6 }}>
          Implementor contract
        </Typography>
        <Typography variant='body2' color='textSecondary'>
          The jar must contain a public class implementing{' '}
          <code>com.c2data.detector.spi.CustomEntityDetector</code> with a public no-arg
          constructor. One instance is shared across all discovery threads, so keep it stateless
          and thread-safe. The jar must be self-contained (bundle helper classes) but must{' '}
          <strong>never</strong> include <code>com.c2data.**</code> classes — the worker supplies
          the SPI types. Each batch runs under a timeout; a detector that repeatedly throws or
          times out is quarantined until it passes a test. A working example lives in the
          discover-api repo under <code>examples/custom-regex-detector/</code>.
        </Typography>
      </div>

      {error && <div className='adv-config-msg-err' style={{ marginBottom: 16 }}>{error}</div>}

      {canEdit && (
        <UploadForm existingNames={existingNames} onUploaded={load} />
      )}

      {loading ? (
        <div>Loading…</div>
      ) : (
        <div className='adv-config-card'>
          <table className='adv-config-table' data-testid='custom-detectors-table'>
            <thead>
              <tr>
                <th>Name</th>
                <th>Class</th>
                <th>Entity types</th>
                <th style={{ width: 120 }}>Status</th>
                <th>Last test</th>
                <th style={{ width: 160 }} />
              </tr>
            </thead>
            <tbody>
              {detectors.map((d) => (
                <tr key={d.name} data-testid={`custom-detector-row-${d.name}`}>
                  <td>
                    <div>{d.name}</div>
                    {d.description && (
                      <div style={{ fontSize: 12, color: INK[500] }}>{d.description}</div>
                    )}
                  </td>
                  <td style={{ fontSize: 12, color: INK[500] }}>{d.className}</td>
                  <td>
                    <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                      {(d.entityTypes || []).map((t) => (
                        <Chip key={t} size='small' label={t} className='adv-config-mono' />
                      ))}
                      {(!d.entityTypes || d.entityTypes.length === 0) && (
                        <span style={{ fontSize: 12, color: INK[500] }}>—</span>
                      )}
                    </div>
                  </td>
                  <td><StatusChip detector={d} /></td>
                  <td style={{ fontSize: 12 }}>{lastTestSummary(d)}</td>
                  <td>
                    <div style={{ display: 'flex', gap: 8 }}>
                      <button
                        type='button'
                        className='adv-config-btn-secondary adv-config-btn'
                        data-testid={`custom-detector-test-${d.name}`}
                        disabled={!canEdit}
                        onClick={() => setTestTarget(d)}
                      >
                        Test
                      </button>
                      <button
                        type='button'
                        className='adv-config-btn-secondary adv-config-btn'
                        data-testid={`custom-detector-delete-${d.name}`}
                        disabled={!canEdit}
                        onClick={() => setDeleteTarget(d.name)}
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {detectors.length === 0 && (
                <tr><td colSpan={6} style={{ color: INK[500] }}>No custom detectors uploaded yet.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {testTarget && (
        <TestDialog
          detector={testTarget}
          onClose={() => setTestTarget(null)}
          onTested={load}
        />
      )}

      {deleteTarget && (
        <DeleteDialog
          name={deleteTarget}
          onClose={() => setDeleteTarget(null)}
          onDeleted={load}
        />
      )}
    </div>
  );
};

// Upload form: file + name + fully-qualified class name + optional description. The replace
// checkbox only appears once the typed name collides with an existing row. Because the worker
// smoke-tests at upload, a successful upload means the detector is already active — there is
// no separate "run a test to activate" step.
const UploadForm = ({ existingNames, onUploaded }) => {
  const [file, setFile] = useState(null);
  const [fileInputKey, setFileInputKey] = useState(0);
  const [name, setName] = useState('');
  const [className, setClassName] = useState('');
  const [description, setDescription] = useState('');
  const [replace, setReplace] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [message, setMessage] = useState(null);

  const trimmedName = name.trim();
  const trimmedClassName = className.trim();
  const nameValid = NAME_REGEX.test(trimmedName);
  const nameInvalid = trimmedName !== '' && !nameValid;
  const isCollision = nameValid && existingNames.has(trimmedName);
  const canSubmit = !!file && nameValid && trimmedClassName !== '' && (!isCollision || replace);

  const resetError = () => {
    setSubmitError(null);
    setMessage(null);
  };

  const handleSubmit = async () => {
    if (!canSubmit || submitting) return;
    resetError();
    setSubmitting(true);
    try {
      const saved = await uploadCustomDetector({
        file,
        name: trimmedName,
        className: trimmedClassName,
        description: description.trim() || undefined,
        replace: isCollision && replace,
      });
      const types = saved && Array.isArray(saved.entityTypes) && saved.entityTypes.length > 0
        ? ` Emits ${saved.entityTypes.join(', ')}.`
        : '';
      setMessage(`Uploaded "${trimmedName}" — smoke test passed, detector is active.${types}`);
      setFile(null);
      setFileInputKey((k) => k + 1);
      setName('');
      setClassName('');
      setDescription('');
      setReplace(false);
      if (onUploaded) onUploaded();
    } catch (e) {
      if (e?.status === 422) {
        setSubmitError({
          headline: e?.body?.error || e?.message || 'The jar was rejected — nothing was kept.',
          kind: e?.body?.kind,
          stackSummary: joinStack(e?.body?.stackSummary),
        });
      } else if (e?.status === 409) {
        setSubmitError({ headline: e?.body?.error || e?.body?.message || 'A detector with that name already exists.' });
      } else {
        setSubmitError({ headline: e?.body?.error || e?.body?.message || e?.message || 'Upload failed' });
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className='adv-config-card'>
      <h2 style={{ fontSize: 15, margin: '0 0 12px' }}>Upload a custom detector</h2>
      <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'flex-start' }}>
        <TextField
          inputProps={{ 'data-testid': 'custom-detector-name' }}
          label='Detector name'
          placeholder='e.g. employee_id'
          value={name}
          onChange={(e) => { setName(e.target.value); resetError(); }}
          error={nameInvalid}
          helperText={
            nameInvalid
              ? 'Must start with a letter and contain only letters, numbers, and underscores (3-64 characters).'
              : ' '
          }
          style={{ minWidth: 260 }}
          margin='dense'
        />
        <TextField
          inputProps={{ 'data-testid': 'custom-detector-classname' }}
          label='Fully-qualified class name'
          placeholder='com.example.detector.EmployeeIdDetector'
          value={className}
          onChange={(e) => { setClassName(e.target.value); resetError(); }}
          style={{ minWidth: 320 }}
          margin='dense'
        />
        <TextField
          inputProps={{ 'data-testid': 'custom-detector-description' }}
          label='Description (optional)'
          value={description}
          onChange={(e) => { setDescription(e.target.value); resetError(); }}
          style={{ minWidth: 260 }}
          margin='dense'
        />
        <div style={{ marginTop: 20 }}>
          <input
            key={fileInputKey}
            data-testid='custom-detector-file'
            type='file'
            accept='.jar'
            onChange={(e) => { setFile(e.target.files?.[0] || null); resetError(); }}
          />
        </div>
      </div>

      {isCollision && (
        <FormControlLabel
          control={
            <Checkbox
              inputProps={{ 'data-testid': 'custom-detector-replace' }}
              checked={replace}
              onChange={(e) => setReplace(e.target.checked)}
            />
          }
          label={`A detector named "${trimmedName}" already exists — check to replace it.`}
          style={{ marginTop: 4, display: 'block' }}
        />
      )}

      <div style={{ marginTop: 12, display: 'flex', alignItems: 'center', gap: 12 }}>
        <Button
          data-testid='custom-detector-upload'
          variant='contained'
          color='primary'
          size='small'
          disabled={submitting || !canSubmit}
          onClick={handleSubmit}
        >
          {submitting ? 'Uploading & testing…' : 'Upload'}
        </Button>
        {message && <span className='adv-config-msg-ok'>{message}</span>}
      </div>

      {submitError && (
        <div className='adv-config-msg-err' style={{ marginTop: 10 }} data-testid='custom-detector-upload-error'>
          <div>{submitError.headline}</div>
          {submitError.kind && (
            <div style={{ fontSize: 12, marginTop: 4 }} data-testid='custom-detector-upload-error-kind'>
              Kind: {submitError.kind}
            </div>
          )}
          {submitError.stackSummary && (
            <details style={{ marginTop: 6 }}>
              <summary style={{ cursor: 'pointer', fontSize: 12 }}>Stack summary</summary>
              <pre
                className='adv-config-mono'
                data-testid='custom-detector-upload-error-stack'
                style={{ maxHeight: 240, overflow: 'auto', marginTop: 6 }}
              >
                {submitError.stackSummary}
              </pre>
            </details>
          )}
        </div>
      )}
    </div>
  );
};

const SHAPE_LABELS = {
  columnar: 'Columnar rows (tables, CSV, Excel, Parquet)',
  json: 'JSON leaves',
  xml: 'XML leaves',
  document: 'Document text chunks',
};

// Per-row test dialog. No inputs — the worker runs its built-in sample batches for every shape
// and reports what the detector found on each. A passing test lifts quarantine / reactivates.
const TestDialog = ({ detector, onClose, onTested }) => {
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const handleRun = async () => {
    setError(null);
    setResult(null);
    setRunning(true);
    try {
      const res = await testCustomDetector(detector.name);
      setResult(res);
      if (onTested) onTested();
    } catch (e) {
      setError(e?.body?.error || e?.body?.message || e?.message || 'Test failed');
    } finally {
      setRunning(false);
    }
  };

  const shapes = result && result.detectionsByShape ? Object.keys(result.detectionsByShape) : [];

  return (
    <Dialog open onClose={onClose} maxWidth='sm' fullWidth data-testid={`custom-detector-test-dialog-${detector.name}`}>
      <DialogTitle>Test {detector.name}</DialogTitle>
      <DialogContent>
        <Typography variant='body2' color='textSecondary'>
          Runs the worker's built-in sample batches (columnar, JSON, XML, document) through this
          detector. Finding nothing is fine — the test fails only if the detector throws or times
          out. A passing test restores a quarantined or inactive detector.
        </Typography>

        {result && (
          <div style={{ marginTop: 14 }} data-testid='custom-detector-test-result'>
            {result.success ? (
              <>
                <div className='adv-config-msg-ok'>
                  Passed — detector is active
                  {Array.isArray(result.entityTypes) && result.entityTypes.length > 0
                    ? ` and emits ${result.entityTypes.join(', ')}.`
                    : '.'}
                </div>
                {shapes.length > 0 && (
                  <table className='adv-config-table' style={{ marginTop: 10 }}>
                    <tbody>
                      {shapes.map((shape) => {
                        const found = result.detectionsByShape[shape] || [];
                        return (
                          <tr key={shape} data-testid={`custom-detector-test-shape-${shape}`}>
                            <td style={{ fontSize: 12, whiteSpace: 'nowrap' }}>{SHAPE_LABELS[shape] || shape}</td>
                            <td style={{ fontSize: 12 }}>
                              {found.length === 0
                                ? <span style={{ color: INK[500] }}>nothing found</span>
                                : found.map((f) => <div key={f} className='adv-config-mono'>{f}</div>)}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                )}
              </>
            ) : (
              <>
                <div className='adv-config-msg-err'>{result.error || 'Test failed.'}</div>
                {result.stackSummary && (
                  <pre className='adv-config-mono' style={{ maxHeight: 240, overflow: 'auto', marginTop: 8 }}>
                    {joinStack(result.stackSummary)}
                  </pre>
                )}
              </>
            )}
          </div>
        )}
        {error && <div className='adv-config-msg-err' style={{ marginTop: 12 }}>{error}</div>}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
        <Button
          data-testid='custom-detector-test-run'
          variant='contained'
          color='primary'
          disabled={running}
          onClick={handleRun}
        >
          {running ? 'Running…' : 'Run smoke test'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// Delete confirm dialog. No force/referencedBy branch: a detector's entity types may be keys in
// the entity→algorithm map, but an orphaned key is harmless (that type just stops being emitted).
const DeleteDialog = ({ name, onClose, onDeleted }) => {
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState(null);

  const doDelete = async () => {
    setDeleting(true);
    setError(null);
    try {
      await deleteCustomDetector(name);
      if (onDeleted) onDeleted();
      // Unmounts this dialog — no further state updates below on success.
      onClose();
      return;
    } catch (e) {
      setError(e?.body?.error || e?.body?.message || e?.message || 'Delete failed');
    }
    setDeleting(false);
  };

  return (
    <Dialog open onClose={onClose} data-testid={`custom-detector-delete-dialog-${name}`}>
      <DialogTitle>{`Delete ${name}?`}</DialogTitle>
      <DialogContent>
        <Typography>
          {`Delete the detector "${name}"? Its entity types will no longer be emitted by discovery. Existing findings are kept. This cannot be undone.`}
        </Typography>
        {error && <div className='adv-config-msg-err' style={{ marginTop: 10 }}>{error}</div>}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={deleting}>Cancel</Button>
        <Button
          data-testid={`custom-detector-confirm-delete-${name}`}
          color='secondary'
          variant='contained'
          disabled={deleting}
          onClick={doDelete}
        >
          {deleting ? 'Deleting…' : 'Delete'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CustomDetectorsScreen;
